import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  alpha,
  useTheme,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SchoolIcon from "@mui/icons-material/School";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CustomSelect from "../FormFields/CustomSelect/CustomSelect";
import { candidateSkillOptions } from "./candidate.config";
import CustomDate from "../CustomForm/CustomDate";
import CustomInput from "../FormFields/CustomInput";
import { createCandidateSkill } from "../../api/services/candidate";
import { minioService } from "../../api/services/minioService";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../base/qk";

const AddCandidateSkill = ({ open, handleClose, candidateId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      candidate: candidateId,
      skill: "",
      expiry_date: "",
      notes: "",
      documents: [],
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      clearErrors();
      setDocuments([]);
      setUploadProgress(0);
    }
  }, [open, reset, clearErrors]);

  const skillOptions = candidateSkillOptions.map((item) => ({
    value: item.value || item,
    label: item.name || item,
  }));

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const validFiles = newFiles.filter((file) => {
        if (!validTypes.includes(file.type)) {
          enqueueSnackbar(`${file.name} is not a valid file type`, { variant: "error" });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          enqueueSnackbar(`${file.name} exceeds 5MB limit`, { variant: "error" });
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        setDocuments((prev) => [...prev, ...validFiles]);
        setValue("documents", [...documents, ...validFiles]);
        clearErrors("documents");
      }
    }
    e.target.value = null;
  };

  const handleRemoveDocument = (index) => {
    const updated = documents.filter((_, i) => i !== index);
    setDocuments(updated);
    setValue("documents", updated);
    if (updated.length === 0) {
      clearErrors("documents");
    }
  };

  const formatBytes = (bytes) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const uploadDocumentsToMinio = async (files) => {
    const uploadedUrls = [];
    const total = files.length;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const response = await minioService.uploadFile(file, "candidate_skills");
      if (response.success) {
        uploadedUrls.push(response.data.url);
        setUploadProgress(Math.round(((i + 1) / total) * 100));
      } else {
        throw new Error(`Failed to upload ${file.name}`);
      }
    }
    return uploadedUrls;
  };

  const onSubmit = async (data) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      let documentUrls = [];
      if (documents.length > 0) {
        documentUrls = await uploadDocumentsToMinio(documents);
      }

      const payload = {
        candidate: parseInt(data.candidate),
        skill: data.skill,
        expiry_date: data.expiry_date || null,
        notes: data.notes || "",
        documents: documentUrls,
      };

      const response = await createCandidateSkill(payload);
      if (response.status === 201) {
        enqueueSnackbar("Skill added successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.candidateSkills, candidateId]);
        handleClose();
      } else {
        enqueueSnackbar("Failed to add skill", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to upload documents", { variant: "error" });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          bgcolor: "#ffffff",
          borderRadius: 2,
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          p: 0,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
            borderBottom: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1A1A2E">
                Add Skill
              </Typography>
              <Typography variant="caption" color="#6B7280">
                Add a new skill to the candidate profile
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Controller
            name="skill"
            control={control}
            rules={{ required: "Skill is required" }}
            render={({ field }) => (
              <CustomSelect
                options={skillOptions}
                label="Skill *"
                name="skill"
                value={field.value}
                onChange={field.onChange}
                placeholder="Select a skill"
                fullWidth
                size="small"
                error={!!errors.skill}
                helperText={errors.skill?.message}
              />
            )}
          />

          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="#475467"
              sx={{ display: "block", mb: 1 }}
            >
              Upload Documents (PDF, DOC, DOCX)
            </Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#2469F0",
                  bgcolor: alpha("#2469F0", 0.04),
                },
              }}
            >
              Browse Files...
              <input
                hidden
                accept=".pdf,.doc,.docx"
                type="file"
                multiple
                onChange={handleFileChange}
              />
            </Button>

            {uploading && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" color="#6B7280">
                    Uploading documents...
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color="#2469F0">
                    {uploadProgress}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: 4,
                    bgcolor: "#E2E8F0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${uploadProgress}%`,
                      height: "100%",
                      bgcolor: "#2469F0",
                      borderRadius: 2,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            )}

            {documents.length > 0 && (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {documents.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      bgcolor: "#F8FAFC",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                      <Box>
                        <Typography variant="caption" fontWeight={500} color="#1A1A2E">
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="#94A3B8" sx={{ display: "block", fontSize: "0.6rem" }}>
                          {formatBytes(file.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveDocument(index)}
                      disabled={uploading}
                      sx={{ color: "#EF4444", p: 0.5 }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}

            {errors.documents && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.documents.message}
              </Typography>
            )}
            <Typography variant="caption" color="#94A3B8" sx={{ mt: 0.5, display: "block" }}>
              Supported formats: PDF, DOC, DOCX (Max size: 5MB per file)
            </Typography>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Controller
              name="expiry_date"
              control={control}
              render={({ field }) => (
                <CustomDate
                  value={field.value}
                  onChange={field.onChange}
                  label="Expiry Date"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label="Notes"
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  placeholder="Additional notes about this skill..."
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={uploading}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                borderColor: "#D0D5DD",
                color: "#344054",
                px: 3,
                "&:hover": {
                  bgcolor: "#F9FAFB",
                  borderColor: "#98A2B3",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : null}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                bgcolor: "#2469F0",
                px: 4,
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                },
              }}
            >
              {uploading ? "Uploading..." : "Add Skill"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCandidateSkill;